import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Play, Clock, Package, PackageOpen, IndianRupee, Truck, AlertTriangle, Plus, XCircle, Layers, Building2, Info, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { getWOStatus } from './WORegisterView';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const processStatusColors = {
  not_started: 'bg-slate-100 text-slate-700',
  issued: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-blue-50 text-blue-700',
  completed: 'bg-emerald-50 text-emerald-700',
  received: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-700',
  qc_failed: 'bg-red-50 text-red-700',
  skipped: 'bg-orange-50 text-orange-700'
};

function getProcessStatus(process, woQty) {
  if (process.status === 'cancelled') return { status: 'cancelled', label: 'Cancelled' };
  if (process.status === 'skipped') return { status: 'skipped', label: 'Skipped' };
  const jobs = process.jobs || [];
  const totalCompleted = jobs.reduce((s, j) => s + (j.quantity_completed || 0), 0);
  const totalIssued = jobs.reduce((s, j) => s + (j.quantity_issued || 0), 0);
  if (totalCompleted >= woQty && woQty > 0) return { status: 'completed', label: 'Completed' };
  if (totalIssued > totalCompleted) return { status: 'in_progress', label: 'In Progress' };
  if (totalIssued > 0) return { status: 'issued', label: 'Issued' };
  return { status: 'not_started', label: 'Not Started' };
}

export default function WODetailView({
  workOrder: wo,
  onBack,
  onSelectProcess,
  onOpenIssueDialog,
  onOpenSkipProcess,
  onOpenAddAdhocProcess,
  onOpenCloseDialog,
  onFetchShortageItems,
  // Pooling
  selectedProcesses,
  onToggleProcessSelection,
  canSelectForPooling,
}) {
  const { getAuthHeader } = useAuth();
  const [materialSummary, setMaterialSummary] = useState(null);
  const [materialHistory, setMaterialHistory] = useState([]);
  const [linkedOJCs, setLinkedOJCs] = useState([]);
  const [costData, setCostData] = useState(null);
  const [activeTab, setActiveTab] = useState('processes');
  // Bug 7 — per-process consumption + BPD expandable
  const [expandedProcess, setExpandedProcess] = useState({});
  const toggleProcessExpand = (pid) => setExpandedProcess(prev => ({ ...prev, [pid]: !prev[pid] }));
  // Bug 4 — SFG parent chain. Fetched lazily when wo.is_sfg_wo.
  const [parentChain, setParentChain] = useState(null);
  const [parentChainError, setParentChainError] = useState(null);
  // Bug 4 redesign — per-SO customer_name lookup map for FNG multi-SO
  // clubbing (one WO across SOs of different customers). Fetched only
  // when there are 2+ distinct sales_order_ids in wo.so_allocations.
  const [soCustomers, setSoCustomers] = useState({});

  useEffect(() => {
    if (!wo || !wo.is_sfg_wo || !wo.id) { setParentChain(null); setParentChainError(null); return; }
    let cancelled = false;
    axios.get(`${API_URL}/work-orders/${wo.id}/parent-chain`, { headers: getAuthHeader() })
      .then(r => { if (!cancelled) setParentChain(r.data); })
      .catch(e => { if (!cancelled) setParentChainError(e?.response?.data?.detail || 'Failed to load parent chain'); });
    return () => { cancelled = true; };
  }, [wo?.id, wo?.is_sfg_wo]);

  // Fetch customer_name per distinct SO for FNG multi-SO. Skips when
  // SFG (chain endpoint already includes customer_name) or single-SO.
  useEffect(() => {
    if (!wo || wo.is_sfg_wo) { setSoCustomers({}); return; }
    const allocs = wo.so_allocations || [];
    const ids = [...new Set(allocs.map(a => a && a.sales_order_id).filter(Boolean))];
    if (ids.length <= 1) { setSoCustomers({}); return; }
    let cancelled = false;
    Promise.all(ids.map(sid =>
      axios.get(`${API_URL}/sales-orders/${sid}`, { headers: getAuthHeader() })
        .then(r => [sid, (r.data && r.data.customer_name) || ''])
        .catch(() => [sid, ''])
    )).then(pairs => {
      if (!cancelled) setSoCustomers(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [wo?.id, wo?.is_sfg_wo]);


  useEffect(() => {
    if (wo?.id) {
      fetchMaterialSummary();
      fetchMaterialHistory();
      fetchLinkedOJCs();
      fetchCostData();
    }
  }, [wo?.id]);

  const fetchMaterialSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/work-orders/${wo.id}/material-summary`, { headers: getAuthHeader() });
      setMaterialSummary(res.data);
    } catch (e) { /* optional */ }
  };

  const fetchMaterialHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/store/bin-card-issues?work_order_id=${wo.id}`, { headers: getAuthHeader() });
      setMaterialHistory(res.data);
    } catch (e) { /* optional */ }
  };

  const fetchLinkedOJCs = async () => {
    try {
      const res = await axios.get(`${API_URL}/ojc/register?work_order_id=${wo.id}`, { headers: getAuthHeader() });
      setLinkedOJCs(res.data);
    } catch (e) { /* optional */ }
  };

  const fetchCostData = async () => {
    try {
      const res = await axios.get(`${API_URL}/work-orders/${wo.id}/actual-production-cost`, { headers: getAuthHeader() });
      setCostData(res.data);
    } catch (e) { /* optional */ }
  };

  // Bug 4 redesign — hooks-rule-of-react placement: these useMemo calls
  // MUST run unconditionally on every render, BEFORE any early return.
  // Each body handles `wo` being null/undefined and returns a sensible
  // empty default. Computed values are then consumed in the JSX below
  // (after the `if (!wo) return null;` guard, which is fine for plain
  // consts but disallowed for hooks). See React Rules of Hooks.
  const linkedSOs = useMemo(() => {
    if (!wo) return [];
    const isSfg = !!wo.is_sfg_wo;
    if (isSfg) {
      const rows = [];
      for (const p of (parentChain?.parents || [])) {
        for (const s of (p.sos || [])) {
          rows.push({
            sales_order_id: s.sales_order_id,
            so_number: s.so_number,
            customer_name: s.customer_name || '',
            line_item_code: s.line_item_code || '',
            delivery_date: s.so_delivery_date,
            quantity: p.allocated_qty,
            parent_wo_number: p.parent_wo_number,
          });
        }
      }
      return rows;
    }
    const allocs = wo.so_allocations || [];
    if (allocs.length > 0) {
      return allocs.map(a => ({
        sales_order_id: a.sales_order_id,
        so_number: a.so_number,
        customer_name: soCustomers[a.sales_order_id] || wo.customer_name || '',
        line_item_code: a.so_line_code || '',
        delivery_date: a.delivery_date,
        quantity: a.quantity,
      }));
    }
    if (wo.sales_order_id) {
      return [{
        sales_order_id: wo.sales_order_id,
        so_number: wo.so_number,
        customer_name: wo.customer_name || '',
        line_item_code: wo.line_item_code || '',
        delivery_date: wo.delivery_date,
        quantity: wo.quantity,
      }];
    }
    return [];
  }, [wo, parentChain, soCustomers]);

  const linkedSOsCount = useMemo(() => {
    if (!wo) return 0;
    if (wo.is_sfg_wo) return parentChain?.rollup?.distinct_so_count ?? 0;
    const ids = new Set();
    for (const r of linkedSOs) { if (r.sales_order_id) ids.add(r.sales_order_id); }
    return ids.size;
  }, [wo, parentChain, linkedSOs]);

  const earliestDeliveryDate = useMemo(() => {
    if (!wo) return null;
    if (wo.is_sfg_wo && parentChain?.rollup?.earliest_so_delivery_date) {
      return parentChain.rollup.earliest_so_delivery_date;
    }
    const dates = linkedSOs.map(r => r.delivery_date).filter(Boolean).sort();
    return dates[0] || wo.delivery_date || null;
  }, [wo, parentChain, linkedSOs]);

  if (!wo) return null;

  const woStatus = getWOStatus(wo);
  const processes = wo.production_processes || [];
  const completedQty = processes.length > 0 ? Math.min(...processes.map(p => p.completed_quantity || 0)) : 0;
  const progressPct = wo.quantity > 0 ? Math.min(100, Math.round((completedQty / wo.quantity) * 100)) : 0;
  const isWOActive = !wo.is_closed && wo.status !== 'received' && wo.status !== 'completed' && wo.status !== 'closed';

  // Bug 4 redesign — unified multi-SO detection + linked-SOs derivation
  // covering BOTH FNG-multi-SO (wo.so_allocations) and SFG (parent chain).
  const soAllocations = wo.so_allocations || [];
  const isSfgWO = !!wo.is_sfg_wo;
  // Multi-SO display rule per spec: FNG with >1 so_allocations OR any
  // SFG with linked parents (the chain is multi-hop by nature).
  const isMultiSO = (!isSfgWO && soAllocations.length > 1)
    || (isSfgWO && (wo.wo_allocations || []).length > 0);

  // Calculate days remaining
  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    try {
      const deliveryDate = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deliveryDate.setHours(0, 0, 0, 0);
      const diffTime = deliveryDate - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const woStatusColors = {
    created: 'bg-slate-100 text-slate-700', issued: 'bg-blue-50 text-blue-700',
    in_progress: 'bg-amber-50 text-amber-700', completed: 'bg-emerald-50 text-emerald-700',
    received: 'bg-emerald-50 text-emerald-700', closed: 'bg-red-50 text-red-700',
    qc_failed: 'bg-red-50 text-red-700',
  };

  return (
    <div className="space-y-5">
      {/* Back + Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-0.5 shrink-0" data-testid="wo-detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-semibold tracking-tight">{wo.work_order_number}</h2>
            <Badge variant="outline" className={woStatusColors[woStatus.status]}>{woStatus.label}</Badge>
            {wo.wo_type === 'child' && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">Child WO</Badge>}
            {wo.is_closed && <Badge variant="destructive" className="text-xs">Closed: {wo.close_reason}</Badge>}
          </div>
          <p className="text-base font-medium text-muted-foreground mt-0.5">{wo.item_name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isWOActive && (
            <>
              <Button size="sm" variant="outline" onClick={() => onOpenAddAdhocProcess(wo)}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Ad-hoc Process
              </Button>
              <Button size="sm" variant="outline" onClick={() => onFetchShortageItems(wo.id, wo.work_order_number)}>
                <Layers className="w-3.5 h-3.5 mr-1" /> Child WOs
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => onOpenCloseDialog(wo)}>
                <XCircle className="w-3.5 h-3.5 mr-1" /> Close
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Quantity" value={`${wo.quantity} ${wo.unit || ''}`} />
        <SummaryCard label="Completed" value={`${completedQty} / ${wo.quantity}`}>
          <Progress value={progressPct} className="h-1.5 mt-1" />
        </SummaryCard>
        
        {/* SO Reference — multi-SO collapses to a "Multi-SO" badge with
            count; single-SO shows the SO number. Full per-SO detail is
            in the new "Linked SOs" tab below, so no popover here. */}
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">SO Reference</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {isMultiSO ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold" data-testid="multi-so-indicator">
                Multi-SO
                <Badge variant="outline" className="text-xs">{linkedSOsCount}</Badge>
              </span>
            ) : (
              <span className="text-sm font-semibold">
                {(linkedSOs[0] && (linkedSOs[0].so_number || linkedSOs[0].line_item_code))
                  || wo.so_number || wo.work_order_number || '-'}
              </span>
            )}
          </div>
        </div>

        
        <SummaryCard
          label={isMultiSO ? 'Earliest Delivery' : 'Delivery Date'}
          value={formatDate(earliestDeliveryDate)}
        />
        <SummaryCard label="Processes" value={`${processes.length} step${processes.length !== 1 ? 's' : ''}`} />
      </div>

      {wo.specs && (
        <div className="text-sm text-muted-foreground bg-muted/40 rounded-lg px-4 py-2">
          <span className="font-medium">Specs:</span> {typeof wo.specs === 'string' ? wo.specs : Object.values(wo.specs).join(', ')}
        </div>
      )}
      {wo.remarks && (
        <div className="text-sm text-orange-700 bg-orange-50 rounded-lg px-4 py-2">
          <span className="font-medium">Remarks:</span> {wo.remarks}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-xl grid-cols-4" data-testid="wo-detail-tabs">
          <TabsTrigger value="processes" data-testid="tab-processes">Processes</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="costs" data-testid="tab-costs">Costs</TabsTrigger>
          <TabsTrigger value="linked-sos" data-testid="tab-linked-sos">
            Linked SOs
            {linkedSOsCount > 0 && (
              <Badge variant="outline" className="ml-2 text-xs">{linkedSOsCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* PROCESSES TAB */}
        <TabsContent value="processes" className="mt-4">
          <div className="grid gap-3">
            {processes.map((process, pIdx) => {
              const pStatus = getProcessStatus(process, wo.quantity);
              const jobs = process.jobs || [];
              const totalIssued = jobs.reduce((s, j) => s + (j.quantity_issued || 0), 0);
              const totalCompleted = jobs.reduce((s, j) => s + (j.quantity_completed || 0), 0);
              const pProgressPct = wo.quantity > 0 ? Math.min(100, Math.round((totalCompleted / wo.quantity) * 100)) : 0;
              const isWOCompleted = wo.status === 'closed' || wo.status === 'received' || wo.status === 'completed' || wo.is_closed;
              const isProcessCompleted = totalCompleted >= wo.quantity || (process.completed_quantity || 0) >= wo.quantity;
              const canCreateJob = (wo.quantity - totalIssued) > 0 && process.status !== 'cancelled' && process.status !== 'skipped' && !isWOCompleted && !isProcessCompleted;
              const canSkip = process.status === 'not_started' && jobs.length === 0 && !isWOCompleted;
              const processKey = `${wo.id}-${process.id}`;
              const isPoolable = canSelectForPooling(wo, process);

              return (
                <Card
                  key={process.id}
                  className={`transition-all hover:shadow-md cursor-pointer border ${pStatus.status === 'skipped' ? 'opacity-60' : ''} ${pStatus.status === 'completed' ? 'border-emerald-200' : ''}`}
                  onClick={() => onSelectProcess(process.id)}
                  data-testid={`process-card-${pIdx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Step {pIdx + 1}</span>
                          <h3 className={`font-semibold text-base ${pStatus.status === 'skipped' ? 'line-through' : ''}`}>{process.process_name}</h3>
                          <Badge variant="outline" className={`text-xs ${processStatusColors[pStatus.status]}`}>{pStatus.label}</Badge>
                          {process.is_outsourced && <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700"><Building2 className="w-3 h-3 mr-0.5" /> OJC</Badge>}
                          {process.is_adhoc && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Ad-hoc</Badge>}
                          {process.pooled_job_number && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Pooled: {process.pooled_job_number}</Badge>}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Target: {process.target_time_hours}h</span>
                          <span>Jobs: {jobs.length}</span>
                          <span>Issued: {totalIssued}</span>
                          <span>Completed: {totalCompleted}</span>
                        </div>
                        <Progress value={pProgressPct} className="h-1.5 mt-2 max-w-xs" />
                      </div>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isPoolable && (
                          <Checkbox
                            checked={!!selectedProcesses[processKey]}
                            onCheckedChange={() => onToggleProcessSelection(wo, process)}
                            className="h-4 w-4 border-purple-500"
                            data-testid={`pool-checkbox-${pIdx}`}
                          />
                        )}
                        {canSkip && (
                          <Button size="sm" variant="outline" className="text-orange-600 hover:bg-orange-50" onClick={(e) => { e.stopPropagation(); onOpenSkipProcess(wo, process); }}>
                            Skip
                          </Button>
                        )}
                        {canCreateJob && (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onOpenIssueDialog(wo, process); }} data-testid={`new-job-btn-${pIdx}`}>
                            <Play className="w-3.5 h-3.5 mr-1" /> New Job
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                  {/* Bug 7 — expandable process details (consumption + BPD + completion stamps) */}
                  {(pStatus.status === 'completed' || pStatus.status === 'in_progress' || pStatus.status === 'issued') && jobs.length > 0 && (
                    <div
                      className="border-t bg-muted/20 px-4 py-2 flex items-center justify-between text-xs cursor-pointer hover:bg-muted/40"
                      onClick={(e) => { e.stopPropagation(); toggleProcessExpand(process.id); }}
                      data-testid={`process-expand-${pIdx}`}
                    >
                      <span className="flex items-center gap-1 text-muted-foreground">
                        {expandedProcess[process.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        {expandedProcess[process.id] ? 'Hide' : 'View'} consumption & BPD
                      </span>
                      <span className="text-muted-foreground">
                        {jobs.length} job(s)
                        {(() => {
                          const totalBpd = jobs.reduce((s, j) => s + ((j.bpd_received || []).reduce((x, b) => x + (b.quantity_received || 0), 0)), 0);
                          return totalBpd > 0 ? ` · BPD ${totalBpd}` : '';
                        })()}
                      </span>
                    </div>
                  )}
                  {expandedProcess[process.id] && (
                    <div className="border-t bg-slate-50/50 px-4 py-3 space-y-4" onClick={(e) => e.stopPropagation()} data-testid={`process-expanded-${pIdx}`}>
                      {jobs.map((job, jIdx) => {
                        const issued = job.materials_issued || [];
                        const bpdList = job.bpd_received || [];
                        const issuedTotalValue = issued.reduce((s, m) => s + ((m.quantity_issued || m.quantity || 0) * (m.unit_price || m.price_at_issue || 0)), 0);
                        const bpdTotalValue = bpdList.reduce((s, b) => s + ((b.quantity_received || 0) * (b.price || 0)), 0);
                        return (
                          <div key={job.id || jIdx} className="bg-white rounded border p-3 text-xs">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-mono">{job.jc_number || job.job_number || `Job ${job.job_letter || jIdx + 1}`}</Badge>
                                <Badge variant="outline" className={`text-[10px] ${processStatusColors[job.status] || 'bg-slate-100'}`}>{job.status}</Badge>
                                <span className="text-muted-foreground">Issued: {job.quantity_issued || 0} · Completed: {job.quantity_completed || 0}</span>
                              </div>
                              <div className="text-muted-foreground">
                                {job.issued_at && <>Issued {formatDate(job.issued_at)}</>}
                                {job.completed_at && <> · Completed {formatDate(job.completed_at)}</>}
                              </div>
                            </div>
                            {issued.length > 0 && (
                              <div className="mb-2">
                                <div className="text-[11px] font-semibold text-slate-700 mb-1">Materials Consumed ({issued.length})</div>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-[11px]">
                                    <thead className="bg-slate-100">
                                      <tr>
                                        <th className="text-left px-2 py-1">Item</th>
                                        <th className="text-right px-2 py-1">Qty</th>
                                        <th className="text-right px-2 py-1">Unit Price</th>
                                        <th className="text-right px-2 py-1">Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {issued.map((m, mi) => {
                                        const q = m.quantity_issued || m.quantity || 0;
                                        const p = m.unit_price || m.price_at_issue || 0;
                                        return (
                                          <tr key={mi} className="border-b last:border-0">
                                            <td className="px-2 py-1">{m.item_name}{m.is_additional ? <Badge variant="outline" className="ml-1 text-[9px] bg-amber-50 text-amber-700">Extra</Badge> : null}</td>
                                            <td className="text-right px-2 py-1">{q} <span className="text-muted-foreground">{m.unit}</span></td>
                                            <td className="text-right px-2 py-1">₹{p.toFixed(2)}</td>
                                            <td className="text-right px-2 py-1">₹{(q * p).toFixed(2)}</td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="bg-slate-50 font-semibold">
                                        <td className="px-2 py-1" colSpan={3}>Total</td>
                                        <td className="text-right px-2 py-1">₹{issuedTotalValue.toFixed(2)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {bpdList.length > 0 && (
                              <div>
                                <div className="text-[11px] font-semibold text-slate-700 mb-1">BPD Received ({bpdList.length})</div>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-[11px]">
                                    <thead className="bg-slate-100">
                                      <tr>
                                        <th className="text-left px-2 py-1">Item</th>
                                        <th className="text-right px-2 py-1">Qty</th>
                                        <th className="text-right px-2 py-1">Price</th>
                                        <th className="text-right px-2 py-1">Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {bpdList.map((b, bi) => (
                                        <tr key={bi} className="border-b last:border-0">
                                          <td className="px-2 py-1">{b.item_name || '-'}</td>
                                          <td className="text-right px-2 py-1">{b.quantity_received || 0} <span className="text-muted-foreground">{b.unit || ''}</span></td>
                                          <td className="text-right px-2 py-1">₹{(b.price || 0).toFixed(2)}</td>
                                          <td className="text-right px-2 py-1">₹{((b.quantity_received || 0) * (b.price || 0)).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                      <tr className="bg-slate-50 font-semibold">
                                        <td className="px-2 py-1" colSpan={3}>Total</td>
                                        <td className="text-right px-2 py-1">₹{bpdTotalValue.toFixed(2)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {issued.length === 0 && bpdList.length === 0 && (
                              <div className="text-center text-muted-foreground py-2">No materials issued or BPD received for this job.</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
            {processes.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>No production processes defined for this work order.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* MATERIALS TAB */}
        <TabsContent value="materials" className="mt-4 space-y-6">
          {/* BOM vs Issued summary */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Package className="w-4 h-4" /> BOM vs Issued Summary
              </h3>
              {materialSummary?.materials?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Material</TableHead>
                      <TableHead className="text-right">BOM Qty</TableHead>
                      <TableHead className="text-right">Issued Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Variance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialSummary.materials.map((mat, idx) => {
                      const variance = mat.issued_qty - mat.bom_qty;
                      return (
                        <TableRow key={idx} className={mat.is_additional ? 'bg-amber-50/40' : ''}>
                          <TableCell className="text-sm font-medium">
                            {mat.item_name}
                            {mat.is_additional && <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700">Extra</Badge>}
                          </TableCell>
                          <TableCell className="text-right text-sm">{mat.is_additional ? '-' : mat.bom_qty}</TableCell>
                          <TableCell className="text-right text-sm font-medium">{mat.issued_qty}</TableCell>
                          <TableCell className="text-xs">{mat.unit}</TableCell>
                          <TableCell>
                            {mat.is_additional ? <span className="text-xs text-amber-600">Extra</span>
                              : variance === 0 ? <span className="text-xs text-muted-foreground">-</span>
                              : variance > 0 ? <span className="text-xs text-red-600">+{variance.toFixed(2)} excess</span>
                              : <span className="text-xs text-blue-600">{Math.abs(variance).toFixed(2)} pending</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No materials in BOM or issued yet</p>
              )}
            </CardContent>
          </Card>

          {/* Material Blocking */}
          {wo.material_blocking?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-orange-500" /> Material Blocking (RMT)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {wo.material_blocking.map((mat, idx) => (
                    <div key={idx} className="bg-muted/30 rounded-lg px-3 py-2 text-sm">
                      <p className="font-medium truncate">{mat.item_name}</p>
                      <p className="text-muted-foreground">
                        {mat.blocked_quantity}/{mat.required_quantity} {mat.unit}
                        {mat.blocked_quantity < mat.required_quantity && <AlertTriangle className="w-3 h-3 inline ml-1 text-orange-500" />}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Material Issuance History */}
          {materialHistory.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                  <PackageOpen className="w-4 h-4" /> Issuance History ({materialHistory.length})
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 text-xs">
                      <TableHead>Issue No.</TableHead>
                      <TableHead>Job</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Issued By</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialHistory.map((issue) => (
                      <TableRow key={issue.id} className="text-xs">
                        <TableCell className="font-mono text-emerald-600 font-medium">{issue.issue_no}</TableCell>
                        <TableCell className="font-mono">{issue.job_number}</TableCell>
                        <TableCell>{issue.process_name}</TableCell>
                        <TableCell>
                          {issue.items?.map((item, i) => (
                            <div key={i}>{item.item_name}: <span className="font-medium">{item.qty_issued} {item.item_unit}</span></div>
                          ))}
                        </TableCell>
                        <TableCell>{issue.issued_by_name}</TableCell>
                        <TableCell>{issue.issue_date ? formatDate(issue.issue_date) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Linked OJCs */}
          {linkedOJCs.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                  <Truck className="w-4 h-4" /> Linked OJCs ({linkedOJCs.length})
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 text-xs">
                      <TableHead>OJC No.</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead>Jobber</TableHead>
                      <TableHead className="text-center">Issued</TableHead>
                      <TableHead className="text-center">Received</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedOJCs.map((ojc) => (
                      <TableRow key={ojc.id} className="text-xs">
                        <TableCell className="font-mono text-purple-600 font-medium">{ojc.ojc_number}</TableCell>
                        <TableCell>{ojc.process_name}</TableCell>
                        <TableCell>{ojc.jobber_name || '-'}</TableCell>
                        <TableCell className="text-center">{ojc.quantity_issued}</TableCell>
                        <TableCell className="text-center">{ojc.quantity_received}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {ojc.status === 'material_sent' ? 'Material Sent' : ojc.status === 'partial_received' ? 'Partial' : ojc.status?.charAt(0).toUpperCase() + ojc.status?.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{ojc.total_ojc_cost > 0 ? `₹${ojc.total_ojc_cost.toLocaleString('en-IN')}` : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* COSTS TAB */}
        <TabsContent value="costs" className="mt-4 space-y-4">
          {costData ? (
            <>
              {/* Cost summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <CostCard label="Material Cost" value={costData.cost_breakdown?.material_cost} />
                <CostCard label="OJC Charges" value={costData.cost_breakdown?.ojc_cost} />
                <CostCard label="BPD Deduction" value={costData.cost_breakdown?.bpd_value} negative />
                <CostCard label="Net Production Cost" value={costData.cost_breakdown?.net_production_cost} highlight />
              </div>
              {costData.completed_quantity > 0 && (
                <p className="text-sm text-muted-foreground">
                  Cost per unit ({costData.completed_quantity} completed): <span className="font-semibold">₹{costData.cost_breakdown?.cost_per_unit?.toFixed(2)}</span>
                </p>
              )}

              {/* Material details */}
              {costData.material_details?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Package className="w-4 h-4" /> Material Cost (FIFO)</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead>Material</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Avg Rate</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costData.material_details.map((mat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium text-sm">{mat.item_name}</TableCell>
                            <TableCell className="text-right text-sm">{mat.quantity}</TableCell>
                            <TableCell className="text-sm">{mat.unit}</TableCell>
                            <TableCell className="text-right text-sm">₹{mat.avg_price?.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium text-sm">₹{mat.total_cost?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* OJC details */}
              {costData.ojc_details?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Building2 className="w-4 h-4" /> OJC Labour Charges</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead>OJC</TableHead>
                          <TableHead>Jobber</TableHead>
                          <TableHead>Process</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costData.ojc_details.map((ojc, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium text-sm">{ojc.ojc_number}</TableCell>
                            <TableCell className="text-sm">{ojc.jobber_name}</TableCell>
                            <TableCell className="text-sm">{ojc.process_name}</TableCell>
                            <TableCell className="text-right font-medium text-sm">₹{ojc.total_cost?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* BPD details */}
              {costData.bpd_details?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><PackageOpen className="w-4 h-4" /> By-Products (Deduction)</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead>By-Product</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costData.bpd_details.map((bpd, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium text-sm">{bpd.item_name}</TableCell>
                            <TableCell className="text-right text-sm">{bpd.quantity}</TableCell>
                            <TableCell className="text-right text-sm">₹{bpd.price?.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium text-sm text-emerald-600">-₹{bpd.value?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <p className="text-center py-8 text-muted-foreground">Loading cost data...</p>
          )}
        </TabsContent>

        {/* Bug 4 redesign — Linked SOs tab. Unified view for both
            FNG-multi-SO (data from wo.so_allocations + per-SO customer
            lookup) and SFG (data from /work-orders/{id}/parent-chain).
            Always rendered; single-SO FNG shows one row. */}
        <TabsContent value="linked-sos" className="mt-4 space-y-3">
          {isSfgWO && !parentChain && !parentChainError && (
            <p className="text-sm text-muted-foreground">Loading parent chain...</p>
          )}
          {isSfgWO && parentChainError && (
            <p className="text-sm text-red-600">{parentChainError}</p>
          )}
          {(!isSfgWO || parentChain) && !parentChainError && (
            linkedSOs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No linked sales orders.</p>
            ) : (
              <>
                {isMultiSO && (
                  <p className="text-sm">
                    {isSfgWO && parentChain && (
                      <>
                        <span className="text-muted-foreground">Via:</span>{' '}
                        <span className="font-semibold">
                          {parentChain.parents.length} parent WO
                          {parentChain.parents.length === 1 ? '' : 's'}
                        </span>
                        <span className="text-muted-foreground"> · </span>
                      </>
                    )}
                    <span className="text-muted-foreground">Linked SOs:</span>{' '}
                    <span className="font-semibold">{linkedSOsCount}</span>
                    {earliestDeliveryDate && (
                      <>
                        <span className="ml-3 text-muted-foreground">Earliest delivery:</span>{' '}
                        <span className="font-semibold">{formatDate(earliestDeliveryDate)}</span>
                      </>
                    )}
                  </p>
                )}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>SO Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Line Item Code</TableHead>
                      <TableHead>Delivery Date</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      {isSfgWO && <TableHead>Via Parent WO</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedSOs.map((row, idx) => (
                      <TableRow key={(row.sales_order_id || '') + ':' + (row.line_item_code || idx)}>
                        <TableCell className="font-medium">{row.so_number || '-'}</TableCell>
                        <TableCell>{row.customer_name || '-'}</TableCell>
                        <TableCell>{row.line_item_code || '-'}</TableCell>
                        <TableCell>{formatDate(row.delivery_date)}</TableCell>
                        <TableCell className="text-right">{row.quantity ?? '-'}</TableCell>
                        {isSfgWO && (
                          <TableCell className="text-sm text-muted-foreground">
                            {row.parent_wo_number || '-'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ label, value, children }) {
  return (
    <div className="bg-muted/30 rounded-lg px-4 py-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold mt-0.5">{value}</p>
      {children}
    </div>
  );
}

function CostCard({ label, value, negative, highlight }) {
  const formatted = value != null ? `${negative ? '-' : ''}₹${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '₹0.00';
  return (
    <div className={`rounded-lg px-4 py-3 ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-muted/30'}`}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-base font-semibold mt-0.5 ${negative ? 'text-emerald-600' : highlight ? 'text-emerald-700' : ''}`}>{formatted}</p>
    </div>
  );
}
